import { html, nothing } from 'lit';
import { query, property, state } from 'lit/decorators.js';
import { pdsCustomElement as customElement } from '../../decorators/pds-custom-element';
import { PdsElement } from '../PdsElement';
import styles from './accordion.scss?inline';
import '@principal/design-system-icons-web/chevron-down';
import { requiredSlot } from '../../decorators/requiredSlot';

/**
 * @summary This component is a accordion that toggles content when the header is clicked
 *
 * @slot summary-title Required: Header summary title
 * @slot summary-description Optional: Summary description
 * @slot accordion-content Required: Hidden/shown accordion content
 * @fires pds-accordion-open A custom event dispatched on click when accordion is opened
 * @fires pds-accordion-close A custom event dispatched on click when accordion is closed
 */
@customElement('pds-accordion', {
  category: 'component',
  type: 'component',
  state: 'stable',
  styles,
})
export class PdsAccordion extends PdsElement {
  /**
   * Style variant
   * - **default** renders the standard accordion color variant
   * - **inverted** renders the accordion on dark backgrounds
   * - **strong** renders the strong accordion color variant
   */
  @property()
  variant: 'default' | 'inverted' | 'strong' = 'default';

  /**
   * Whether the accordion is open or not
   */
  @property({ type: Boolean, reflect: true })
  open: boolean = false;

  /**
   * Reduces the top and bottom padding of the label
   */
  @property({ type: Boolean, reflect: true })
  condensed: boolean = false;

  /**
   * The details element that contains the summary and content elements
   * @internal
   */
  @query('details')
  details: HTMLDetailsElement;

  /**
   * The summary element that contains the summary element
   * @internal
   */
  @query('summary')
  summary: HTMLElement;

  /**
   * Returns whether the description slot is empty or not
   * @internal
   */
  @state()
  descriptionPresent: boolean = false;

  updated() {
    this.descriptionPresent = !this.slotEmpty('summary-description');
  }

  /**
   * Because we require animation we have to override the default behavior of the disclosure widget.
   */
  handleClick(e: MouseEvent) {
    e.preventDefault();
    this.toggle();
  }

  /**
   * Toggle the accordion to either open or closed.
   */
  toggle() {
    if (this.isClosing || !this.open) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.open = true;
      } else {
        this.expand();
      }

      this.open = true;

      const customEvent = new CustomEvent('pds-accordion-open', {
        bubbles: true,
        composed: true,
        detail: {
          summary: this.querySelector('[slot]')?.textContent,
        },
      });

      this.dispatchEvent(customEvent);
    } else if (this.isExpanding || this.open) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.open = false;
      } else {
        this.close();
      }

      this.open = false;

      const customEvent = new CustomEvent('pds-accordion-close', {
        bubbles: true,
        composed: true,
        detail: {
          summary: this.querySelector('[slot]')?.textContent,
        },
      });

      this.dispatchEvent(customEvent);
    }
  }

  /**
   * Close the accordion by setting the height of the details element to the height of the summary element
   */
  close() {
    this.isClosing = true;
    this.details.style.overflow = 'hidden';

    const startHeight = `${this.details.offsetHeight}px`;

    const endHeight = `${this.summary.offsetHeight}px`;

    if (this.animation) {
      this.animation.cancel();
      this.isClosing = false;
    }

    this.animation = this.details.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: 400,
        easing: 'ease-out',
      },
    );

    this.animation.onfinish = () => this.onAnimationFinish(false);
  }

  /**
   * Open the accordion by setting the height of the details elememt to the height of the summary and content elements
   */
  expand(): void {
    this.details.style.height = `${this.details.offsetHeight}px`;
    this.open = true;
    this.details.style.overflow = 'hidden';
    window.requestAnimationFrame(() => this.animateExpand());
  }

  /**
   * Start the expand animation
   */
  animateExpand(): void {
    this.isExpanding = true;
    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.details.scrollHeight}px`;

    if (this.animation) {
      this.animation.cancel();
      this.isExpanding = false;
    }

    this.animation = this.details.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: 400,
        easing: 'ease-out',
      },
    );

    this.animation.onfinish = () => this.onAnimationFinish(true);
  }

  /**
   * When the animation is finished ensure that the details element's
   * open attribute is set to the correct value and the height
   * and overflow properties are reset.
   */
  onAnimationFinish(open: boolean) {
    this.open = open;
    // Clear this.animation
    this.animation = null;

    this.isClosing = false;
    this.isExpanding = false;
    this.details.style.height = '';
    this.details.style.overflow = '';
  }

  /**
   * @internal
   */
  private isClosing = false;

  /**
   * @internal
   */
  private isExpanding = false;

  /**
   * @internal
   */
  private animation: Animation | null;

  /**
   * @internal
   */
  get classNames() {
    return {
      'is-active': this.open,
      condensed: this.condensed,
      [this.variant]: !!this.variant,
    };
  }

  @requiredSlot(['summary-title', 'accordion-content'])
  render() {
    return html`<details
      class="${this.getClass()}"
      ?open="${this.open}"
      part="accordion"
    >
      <summary
        @click=${this.handleClick}
        ?condensed="${this.condensed}"
        class="pds-c-accordion__summary"
        part="accordion-summary"
      >
        <slot name="summary-title" part="summary-title"></slot>
        <span
          class="${this.descriptionPresent
            ? this.classEl('description-present')
            : nothing}"
          part="description-present"
        >
          <slot name="summary-description" part="summary-description"></slot>
        </span>
        <span class="pds-c-accordion__icon">
          <pds-icon-chevron-down size="default"> </pds-icon-chevron-down>
        </span>
      </summary>
      <div class="pds-c-accordion__content" part="accordion-content">
        <slot name="accordion-content"></slot>
      </div>
    </details>`;
  }
}
